import { Injectable } from '@nestjs/common';
import { Repository } from 'typeorm';
import { ErrorResponse } from '@gtpl/shared-models/common-models';
import { InjectRepository } from '@nestjs/typeorm';
import { Certifications } from './certifications.entity';
import { CertificationDTO } from './dto/certification.dto';
import { CertificationAdapter } from './dto/certifications.adapter';
import { CertificationResponseModel, AllCertificationsResponse } from '@gtpl/shared-models/hrms';
import { CertificationIdRequest } from './dto/certification.request';
import { LogsService } from '@gtpl/shared-services/user-logs';
import { LogsDto } from 'libs/shared-models/user-logs/src/lib/Logs/logs.dto';


@Injectable()
export class CertificationsService {

    constructor(
      private logService:LogsService,

        @InjectRepository(Certifications)
        private repository: Repository<Certifications>,
        private adapter: CertificationAdapter,
    ) { }

    async getCertificationsWithoutRelations(certification: string): Promise<Certifications> {
        const response = await this.repository.findOne({
            where: { certification: certification },
        });
        if (response) {
            return response;
        } else {
            return null;
        }
    }

    async createCertification(dto: CertificationDTO, isUpdate: boolean): Promise<CertificationResponseModel> {
        try {
            if (!isUpdate) {
                const entity = await this.getCertificationsWithoutRelations(dto.certification);
                if (entity) {
                    throw new ErrorResponse(11104, 'Certification already exists');
                }
            } else {
                const entity = await this.getCertificationsWithoutRelations(dto.certification);
                if (entity) {
                    if (entity.certificationId != dto.certificationId) {
                        throw new ErrorResponse(11104, 'Certification already exists');
                    }
                }
            }
            const convertedDto: Certifications = this.adapter.convertDtoToEntity(dto);
            const savedEntity: Certifications = await this.repository.save(convertedDto);
            const savedDto: CertificationDTO = this.adapter.convertEntityToDto(savedEntity);
            if (savedDto) {
                const response = new CertificationResponseModel(true, 1, isUpdate ? 'Certification updated successfully' : 'Certification created successfully', savedDto);
                const name=isUpdate?'updated':'created'
            
                const newLogDto = new LogsDto(1,name, 'Certifications', savedDto.certificationId, true, '',savedDto.createdUser)
                let res = await this.logService.createLog(newLogDto);
                console.log(res);
                return response;
            } else {
                throw new ErrorResponse(11106, 'Certification saved but there is an issue while converting it into DTO');
            }
        } catch (error) {
            throw error;
        }
    }

    async getAllCertifications(): Promise<AllCertificationsResponse> {
        try {
            const dtos: CertificationDTO[] = [];
            const entities: Certifications[] = await this.repository.find({ order: { createdAt: 'DESC' } });
            if (entities) {
                entities.forEach(entity => {
                    const Dto: Certifications = this.adapter.convertEntityToDto(entity);
                    dtos.push(Dto);
                });
                const response = new AllCertificationsResponse(true, 1, 'Certifications Retrieved Successfully', dtos);
                const newLogDto = new LogsDto(1,'view', 'Certifications', 0, true, '','')
                let res = await this.logService.createLog(newLogDto);
                console.log(res);
                return response;
            } else {
                throw new ErrorResponse(99998, 'Data Not Found');
            }
        } catch (error) {
            throw error;
        }
    }

    async getAllActiveCertifications(): Promise<AllCertificationsResponse> {
        try {
            const dtos: CertificationDTO[] = [];
            const entities: Certifications[] = await this.repository.find({ order: { certification: 'ASC' }, where: { isActive: true } });
            if (entities) {
                entities.forEach(entity => {
                    const Dto: Certifications = this.adapter.convertEntityToDto(entity);
                    dtos.push(Dto);
                });
                const response = new AllCertificationsResponse(true, 1, 'Certifications Retrieved Successfully', dtos);
                return response;
            } else {
                throw new ErrorResponse(99998, 'Data Not Found');
            }
        } catch (error) {
            throw error;
        }
    }

    async getCertificationByCertId(id: number): Promise<Certifications> {
        const response = await this.repository.findOne({ where: { certificationId: id } });
        if (response) {
            return response;
        } else {
            return null;
        }
    }

    async activateOrDeactivateCertification(req: CertificationIdRequest): Promise<CertificationResponseModel> {
        try {
            const certExists = await this.getCertificationByCertId(req.certificationId);
            if (certExists) {
                if (req.versionFlag != certExists.versionFlag) {
                    throw new ErrorResponse(10113, 'Someone updated the current certification informations. Refresh and try again');
                } else {
                    const certStatus = await this.repository.update(
                        { certificationId: req.certificationId },
                        { isActive: req.isActive, updatedUser: req.updatedUser }
                    );
                    if (certExists.isActive) {
                        if (certStatus.affected) {
                            const response: CertificationResponseModel = new CertificationResponseModel(true, 10115, 'Certification de-activated successfully');
                            return response;
                        } else {
                            throw new ErrorResponse(10111, 'Certification is already de-activated');
                        }
                    } else {
                        if (certStatus.affected) {
                            const response: CertificationResponseModel = new CertificationResponseModel(true, 10114, 'Certification activated successfully');
                            return response;
                        } else {
                            throw new ErrorResponse(10112, 'Certification is already activated');
                        }
                    }
                }
            } else {
                throw new ErrorResponse(99998, "No Records Found");
            }
        } catch (error) {
            throw error;
        }
    }

    async getCertificationById(req: CertificationIdRequest): Promise<CertificationResponseModel> {
        try {
            const entity: Certifications = await this.repository.findOne({ where: { certificationId: req.certificationId } });
            if (entity) {
                const convertedEntity = this.adapter.convertEntityToDto(entity);
                const response: CertificationResponseModel = new CertificationResponseModel(true, 1, 'Certification found and retrieved', convertedEntity);
                return response;
            } else {
                throw new ErrorResponse(99998, "No Data Found");
            }
        } catch (error) {
            throw error;
        }
    }
}
